import React, { useState, useEffect } from 'react';
import { 
    Typography, Box, Grid, Paper, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl, InputLabel, Link, Button, TextField, IconButton
} from '@mui/material';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, 
    Tooltip, ResponsiveContainer 
} from 'recharts';
import { adminApi } from '../../services/api';
import { useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const UserActivityWidget = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activities, setActivities] = useState([]);
    const [codesHistory, setCodesHistory] = useState([]);
    const [daysRange, setDaysRange] = useState(30); // Default to 30 days
    const [totalStats, setTotalStats] = useState({
        totalClicks: 0,
        totalUsers: 0,
        newUsers: 0,
        codeOwners: 0
    });
    const [selectedOwnerId, setSelectedOwnerId] = useState(null);
    const [editingOwner, setEditingOwner] = useState(null);
    const [editValue, setEditValue] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const since = new Date();
                since.setDate(since.getDate() - daysRange);

                // Use existing endpoints with date filter
                const [activitiesResponse, codesHistoryResponse] = await Promise.all([
                    adminApi.getUserActivities({ since: since.toISOString() }),
                    adminApi.getCodesHistory({ since: since.toISOString() })
                ]);

                setActivities(activitiesResponse);
                setCodesHistory(codesHistoryResponse);

            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [daysRange]); // Refetch when days range changes

    // Fetch total stats (no date filter)
    const fetchTotalStats = async () => {
        try {
            const [allActivities, allCodeOwners] = await Promise.all([
                adminApi.getUserActivities({ since: new Date(0).toISOString() }),
                adminApi.getCodeOwners()
            ]);

            setTotalStats({
                totalClicks: allActivities.length,
                totalUsers: new Set(allActivities.map(a => a.user_id)).size,
                codeOwners: allCodeOwners.length
            });
        } catch (error) {
            console.error('Error fetching total stats:', error);
        }
    };

    useEffect(() => {
        fetchTotalStats();
    }, []); // Fetch once on mount

    // Process data for stats and charts
    const processData = () => {
        // Filter activities by owner if selected
        const filteredActivities = selectedOwnerId 
            ? activities.filter(a => a.code_owner_id === selectedOwnerId)
            : activities;

        // Calculate stats
        const stats = {
            totalClicks: filteredActivities.length,
            totalUsers: new Set(filteredActivities.map(a => a.user_id)).size,
            newUsers: new Set(
                filteredActivities
                    .filter(a => {
                        const date = new Date(a.timestamp);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return date >= weekAgo;
                    })
                    .map(a => a.user_id)
            ).size,
            codeOwners: new Set(codesHistory.map(c => c.owner_id)).size
        };

        // Process daily clicks
        const clicksByDay = filteredActivities.reduce((acc, activity) => {
            const date = new Date(activity.timestamp).toISOString().split('T')[0];
            acc[date] = (acc[date] || 0) + 1;
            return acc;
        }, {});

        const dailyClicks = Object.entries(clicksByDay)
            .map(([date, clicks]) => ({ date, clicks }))
            .sort((a, b) => a.date.localeCompare(b.date));

        // Process top codes
        const codeStats = filteredActivities.reduce((acc, activity) => {
            const codeId = activity.code_id;
            acc[codeId] = (acc[codeId] || 0) + 1;
            return acc;
        }, {});

        // Process top codes with filtered data if owner selected
        const topCodes = codesHistory
            .filter(code => !selectedOwnerId || code.owner_id === selectedOwnerId)
            .map(code => ({
                ...code,
                total_clicks: codeStats[code.code_id] || 0
            }))
            .sort((a, b) => b.total_clicks - a.total_clicks)
            .slice(0, 10);

        return { stats, dailyClicks, topCodes };
    };

    // Modify the Code Owners section data processing
    const processCodeOwnersData = () => {
        // First, create a map of code clicks from activities
        const codeClicks = activities.reduce((acc, activity) => {
            if (activity.code_id) {
                acc[activity.code_id] = (acc[activity.code_id] || 0) + 1;
            }
            return acc;
        }, {});

        // Group by owner and calculate totals
        const ownersData = codesHistory.reduce((acc, code) => {
            if (!code.owner_id) return acc;

            if (!acc[code.owner_id]) {
                acc[code.owner_id] = {
                    id: code.owner_id,
                    email: code.owner_email,
                    free_codes: code.free_codes || 0,  // Include free_codes from database
                    codes: new Set(),
                    clicks: 0
                };
            }
            
            acc[code.owner_id].codes.add(code.code_value);
            acc[code.owner_id].clicks += codeClicks[code.code_id] || 0;
            
            return acc;
        }, {});

        return Object.values(ownersData);
    };

    const handleOwnerClick = (ownerId) => {
        setSelectedOwnerId(ownerId === selectedOwnerId ? null : ownerId); // Toggle selection
    };

    const handleEditClick = (owner) => {
        setEditingOwner(owner.id);
        setEditValue(owner.free_codes.toString());
    };

    const handleSave = async (ownerId) => {
        try {
            // Add input validation
            const freeCodes = parseInt(editValue);
            if (isNaN(freeCodes) || freeCodes < 0) {
                alert('Please enter a valid number');
                return;
            }

            setLoading(true); // Show loading state
            
            // Use axiosInstance from adminApi instead of fetch
            await adminApi.put(`/api/admin/code-owners/${ownerId}/free-codes`, {
                freeCodes: freeCodes
            });

            // Refresh the data
            const since = new Date();
            since.setDate(since.getDate() - daysRange);
            const [activitiesResponse, codesHistoryResponse] = await Promise.all([
                adminApi.getUserActivities({ since: since.toISOString() }),
                adminApi.getCodesHistory({ since: since.toISOString() })
            ]);

            setActivities(activitiesResponse);
            setCodesHistory(codesHistoryResponse);
            setEditingOwner(null); // Close edit mode
            
            // Optional: Show success message
          //  alert('Free codes updated successfully');
        } catch (error) {
            console.error('Error updating free codes:', error);
            alert('Failed to update free codes: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Typography>Loading...</Typography>;
    if (error) return <Typography color="error">Error: {error}</Typography>;

    const { stats, dailyClicks, topCodes } = processData();

    return (
        <Box sx={{ 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box'
        }}>
            {/* Date Range Selector */}
            <Grid container spacing={isMobile ? 1 : 3} sx={{ width: '100%', m: 0 }}>
                <Grid item xs={12}>
                    <Paper sx={{ 
                        p: isMobile ? 1 : 2,
                        '& .MuiFormControl-root': {
                            width: '100%'  // Make select full width on mobile
                        }
                    }}>
                        <FormControl>
                            <InputLabel>History Range</InputLabel>
                            <Select
                                value={daysRange}
                                label="History Range"
                                onChange={(e) => setDaysRange(e.target.value)}
                            >
                                <MenuItem value={7}>Last 7 days</MenuItem>
                                <MenuItem value={14}>Last 14 days</MenuItem>
                                <MenuItem value={30}>Last 30 days</MenuItem>
                                <MenuItem value={90}>Last 90 days</MenuItem>
                            </Select>
                        </FormControl>
                    </Paper>
                </Grid>
            </Grid>

            {/* Stats Cards */}
            <Grid container spacing={isMobile ? 1 : 3} sx={{ width: '100%', m: 0 }}>
                {/* Make cards stack 2x2 on mobile */}
                <Grid item xs={6} sm={3}>
                    <Paper sx={{ 
                        p: isMobile ? 1 : 2,
                        '& .MuiTypography-h4': {
                            fontSize: isMobile ? '1.5rem' : '2.125rem'
                        }
                    }}>
                        <Typography variant={isMobile ? "h5" : "h4"}>{stats.totalClicks}</Typography>
                        <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                            Clicks
                        </Typography>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'block',
                                color: 'text.secondary',
                                mt: 0.5,
                                pt: 0.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                fontSize: isMobile ? '0.65rem' : '0.75rem'
                            }}
                        >
                            Total: {totalStats.totalClicks}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Paper sx={{ 
                        p: isMobile ? 1 : 2,
                        '& .MuiTypography-h4': {
                            fontSize: isMobile ? '1.5rem' : '2.125rem'
                        }
                    }}>
                        <Typography variant={isMobile ? "h5" : "h4"}>{stats.totalUsers}</Typography>
                        <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                            Users
                        </Typography>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'block',
                                color: 'text.secondary',
                                mt: 0.5,
                                pt: 0.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                fontSize: isMobile ? '0.65rem' : '0.75rem'
                            }}
                        >
                            Total: {totalStats.totalUsers}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Paper sx={{ 
                        p: isMobile ? 1 : 2,
                        '& .MuiTypography-h4': {
                            fontSize: isMobile ? '1.5rem' : '2.125rem'
                        }
                    }}>
                        <Typography variant={isMobile ? "h5" : "h4"}>{stats.newUsers}</Typography>
                        <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                            New Users
                        </Typography>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'block',
                                color: 'text.secondary',
                                mt: 0.5,
                                pt: 0.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                fontSize: isMobile ? '0.65rem' : '0.75rem'
                            }}
                        >
                            Total: {totalStats.totalUsers}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Paper sx={{ 
                        p: isMobile ? 1 : 2,
                        '& .MuiTypography-h4': {
                            fontSize: isMobile ? '1.5rem' : '2.125rem'
                        }
                    }}>
                        <Typography variant={isMobile ? "h5" : "h4"}>{stats.codeOwners}</Typography>
                        <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                            Code Owners
                        </Typography>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'block',
                                color: 'text.secondary',
                                mt: 0.5,
                                pt: 0.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                fontSize: isMobile ? '0.65rem' : '0.75rem'
                            }}
                        >
                            Total: {totalStats.codeOwners}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={isMobile ? 1 : 3} sx={{ width: '100%', m: 0 }}>
                {/* Daily Clicks Chart */}
                <Grid item xs={12}>
                    <Paper sx={{ 
                        width: '100%',
                        overflowX: 'hidden',
                        boxSizing: 'border-box',
                        p: isMobile ? 1 : 2
                    }}>
                        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                            Daily Clicks
                        </Typography>
                        <Box sx={{ 
                            width: '100%',
                            maxWidth: '100%',
                            overflowX: 'hidden'
                        }}>
                            <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                                <LineChart data={dailyClicks}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={(date) => new Date(date).toLocaleDateString()}
                                        tick={{ fontSize: isMobile ? 10 : 12 }}
                                        angle={isMobile ? -45 : 0}
                                        textAnchor={isMobile ? "end" : "middle"}
                                        height={isMobile ? 50 : 30}
                                    />
                                    <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                                    <Tooltip 
                                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                                        contentStyle={{ fontSize: isMobile ? 12 : 14 }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="clicks" 
                                        stroke="#2196f3" 
                                        strokeWidth={isMobile ? 1.5 : 2}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>

                {/* Top Codes Table */}
                <Grid item xs={12}>
                    <Paper sx={{ 
                        width: '100%',
                        overflowX: 'auto',  // Allow table to scroll horizontally if needed
                        boxSizing: 'border-box',
                        p: isMobile ? 1 : 2
                    }}>
                        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                            Top Codes by Clicks
            </Typography>
                        <TableContainer sx={{ 
                            maxWidth: '100%',
                            '& table': {
                                tableLayout: 'fixed',  // Important for mobile
                                width: '100%'
                            }
                        }}>
                            <Table size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="20%">Code</TableCell>
                                        <TableCell width="40%">Name</TableCell>
                                        <TableCell width="20%">Owner</TableCell>
                                        <TableCell width="20%" align="right">Clicks</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topCodes.map((code) => (
                                        <TableRow key={code.code_id}>
                                            <TableCell 
                                                width="20%"
                                                sx={{ 
                                                    p: isMobile ? 1 : 2,
                                                    fontSize: isMobile ? '0.75rem' : 'inherit'
                                                }}
                                            >
                                                {code.code_value}
                                            </TableCell>
                                            <TableCell 
                                                width="40%"
                                                sx={{ 
                                                    p: isMobile ? 1 : 2,
                                                    fontSize: isMobile ? '0.75rem' : 'inherit'
                                                }}
                                            >
                                                <a 
                                                    href={code.code_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
                                                >
                                                    {code.code_name || 'Unnamed Code'}
                                                </a>
                                            </TableCell>
                                            <TableCell 
                                                width="20%"
                                                sx={{ 
                                                    p: isMobile ? 1 : 2,
                                                    fontSize: isMobile ? '0.75rem' : 'inherit'
                                                }}
                                            >
                                                <Link
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => handleOwnerClick(code.owner_id)}
                                                    sx={{ 
                                                        fontSize: isMobile ? '0.75rem' : 'inherit',
                                                        cursor: 'pointer',
                                                        textDecoration: 'none',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {code.owner_id}
                                                </Link>
                                            </TableCell>
                                            <TableCell 
                                                width="20%"
                                                align="right"
                                                sx={{ 
                                                    p: isMobile ? 1 : 2,
                                                    fontSize: isMobile ? '0.75rem' : 'inherit'
                                                }}
                                            >
                                                {code.total_clicks}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Code Owners Section */}
                <Grid item xs={12}>
                    <Paper sx={{ p: isMobile ? 1 : 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant={isMobile ? "subtitle1" : "h6"}>
                                Code Owners Overview
            </Typography>
                            {selectedOwnerId && (
                                <Button 
                                    size="small" 
                                    onClick={() => setSelectedOwnerId(null)}
                                    startIcon={<ClearIcon />}
                                >
                                    Clear Filter
                                </Button>
                            )}
                        </Box>
                        <TableContainer>
                            <Table size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Free Codes</TableCell>
                                        <TableCell align="right">Total Clicks</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {processCodeOwnersData()
                                        .filter(owner => !selectedOwnerId || owner.id === selectedOwnerId)
                                        .map(owner => (
                                            <TableRow 
                                                key={owner.id}
                                                sx={{
                                                    bgcolor: selectedOwnerId === owner.id ? 
                                                        'action.selected' : 'inherit'
                                                }}
                                            >
                                                <TableCell>
                                                    <Link
                                                        component="button"
                                                        onClick={() => handleOwnerClick(owner.id)}
                                                        sx={{ 
                                                            fontSize: isMobile ? '0.75rem' : 'inherit',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {owner.id}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{owner.email}</TableCell>
                                                <TableCell>
                                                    {editingOwner === owner.id ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <TextField
                                                                size="small"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                type="number"
                                                                sx={{ width: 80 }}
                                                            />
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleSave(owner.id)}
                                                                color="primary"
                                                            >
                                                                <SaveIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => setEditingOwner(null)}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {owner.free_codes}
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleEditClick(owner)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {owner.clicks}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserActivityWidget; 